import { AlertTriangle, ExternalLink, CheckCircle } from 'lucide-react';
import { ProjectData } from '../types';
import { resolveSupportContact, supportPhoneTelHref } from '../utils/supportContact';

interface AppleAgreementUpdateProps {
  project: ProjectData;
}

export const AppleAgreementUpdate = ({ project }: AppleAgreementUpdateProps) => {
  const { appType, jiraKey } = project;
  const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
  const support = resolveSupportContact(appType, project.supportContact);

  return (
    <div className="space-y-6">
      {/* Alert Banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-orange-800 mb-2">
              Apple Developer Agreement Update Required
            </h3>
            <p className="text-orange-700 mb-4">
              Apple has updated their Developer Program License Agreement. You must accept this update to continue with your {appName} project.
            </p>
            <div className="bg-white border border-orange-200 rounded-md p-4">
              <p className="text-sm text-orange-800 font-medium mb-2">⏰ Time Limit: 30 days</p>
              <p className="text-sm text-orange-700">
                This update must be completed within 30 days to avoid service interruptions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📋 How to Accept the Apple Developer Agreement Update
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Log into Apple Developer Portal</h3>
              <p className="text-gray-600 text-sm">
                Visit <a href="https://developer.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center">
                  developer.apple.com <ExternalLink className="h-3 w-3 ml-1" />
                </a> and sign in with your Apple Developer account
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Look for Agreement Notifications</h3>
              <p className="text-gray-600 text-sm">
                You'll see a notification banner or alert about the agreement update on the main dashboard or in your account section
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Review and Accept the Agreement</h3>
              <p className="text-gray-600 text-sm">
                Click on the agreement notification, review the updated terms, and accept the new agreement
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              4
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Confirm Completion</h3>
              <p className="text-gray-600 text-sm">
                Once you've accepted the agreement, return to this portal and let us know it's complete
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Link */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          📖 Detailed Instructions & Screenshots
        </h3>
        <p className="text-blue-800 mb-4">
          For step-by-step instructions with screenshots, visit our help section:
        </p>
        <a
          href={`/project/${jiraKey}/help#apple-agreement-updates`}
          className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <span>View Help Guide</span>
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {/* Support Contact */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          🆘 Need Help?
        </h3>
        <p className="text-gray-700 mb-4">
          Our support team is here to assist you with the Apple Developer Agreement update process:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Support</p>
              <p className="text-sm text-gray-600">
                <a href={`mailto:${support.email}`} className="text-blue-600 hover:text-blue-800">
                  {support.email}
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-gray-900">Phone Support</p>
              <p className="text-sm text-gray-600">
                <a href={supportPhoneTelHref(support.phone)} className="text-blue-600 hover:text-blue-800">
                  {support.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Completion Button */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <CheckCircle className="h-6 w-6 text-green-600" />
          <h3 className="text-lg font-semibold text-green-900">
            Agreement Update Complete?
          </h3>
        </div>
        <p className="text-green-800 mb-4">
          Once you've successfully accepted the Apple Developer Agreement update, click the button below to notify us and continue with your project.
        </p>
        <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200">
          Mark Agreement Update Complete
        </button>
      </div>
    </div>
  );
};
